import React, { useState } from 'react';
import { ExportDetail, exportApi } from '../services/api';

type ExportFormat = 'customer' | 'xlsx';

interface ExportDialogProps {
    rfqId: string;
    onClose: () => void;
}

const radioGroupClass = 'flex flex-col gap-2';
const radioLabelClass =
    'flex items-start gap-3 cursor-pointer border border-[#141414]/20 px-3 py-2 hover:border-[#141414]/50 transition';
const radioInputClass = 'mt-1 accent-[#141414]';
const headingClass = 'ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/70';

const ExportDialog: React.FC<ExportDialogProps> = ({ rfqId, onClose }) => {
    const [detail, setDetail] = useState<ExportDetail>('itemized');
    const [format, setFormat] = useState<ExportFormat>('customer');
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleExport = async () => {
        setError(null);
        if (format === 'customer') {
            window.open(
                `/rfqs/${rfqId}/print?detail=${detail}`,
                '_blank',
                'noopener'
            );
            onClose();
            return;
        }

        setIsExporting(true);
        try {
            await exportApi.downloadXlsx(rfqId, detail);
            onClose();
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to download workbook'
            );
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#141414]/50 px-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="export-dialog-title"
        >
            <article className="relative bg-white border border-[#141414]/30 shadow-[0_1px_0_0_#141414] w-full max-w-lg">
                <span className="absolute -top-px -left-px h-3 w-3 border-t-2 border-l-2 border-[#141414]" />
                <span className="absolute -top-px -right-px h-3 w-3 border-t-2 border-r-2 border-[#141414]" />
                <span className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-[#141414]" />
                <span className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-[#141414]" />
                <div className="p-5">
                    <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#e0461e]">
                        Export
                    </div>
                    <h3
                        id="export-dialog-title"
                        className="ff-serif text-2xl leading-none mt-1 mb-4"
                    >
                        Export quote
                    </h3>

                    <div className="mb-5">
                        <div className={headingClass}>Format</div>
                        <div className={`${radioGroupClass} mt-2`}>
                            <label className={radioLabelClass}>
                                <input
                                    type="radio"
                                    name="export-format"
                                    value="customer"
                                    checked={format === 'customer'}
                                    onChange={() => setFormat('customer')}
                                    className={radioInputClass}
                                />
                                <span>
                                    <span className="ff-display text-sm">
                                        RFQ for customer
                                    </span>
                                    <span className="ff-mono text-[11px] text-[#141414]/55 block">
                                        Opens the printable quote in a new tab.
                                    </span>
                                </span>
                            </label>
                            <label className={radioLabelClass}>
                                <input
                                    type="radio"
                                    name="export-format"
                                    value="xlsx"
                                    checked={format === 'xlsx'}
                                    onChange={() => setFormat('xlsx')}
                                    className={radioInputClass}
                                />
                                <span>
                                    <span className="ff-display text-sm">
                                        Excel workbook (internal)
                                    </span>
                                    <span className="ff-mono text-[11px] text-[#141414]/55 block">
                                        Downloads an .xlsx with procurement actuals and variance.
                                    </span>
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="mb-5">
                        <div className={headingClass}>Detail level</div>
                        <div className={`${radioGroupClass} mt-2`}>
                            <label className={radioLabelClass}>
                                <input
                                    type="radio"
                                    name="export-detail"
                                    value="itemized"
                                    checked={detail === 'itemized'}
                                    onChange={() => setDetail('itemized')}
                                    className={radioInputClass}
                                />
                                <span>
                                    <span className="ff-display text-sm">Itemized</span>
                                    <span className="ff-mono text-[11px] text-[#141414]/55 block">
                                        Per-equipment lines under each station.
                                    </span>
                                </span>
                            </label>
                            <label className={radioLabelClass}>
                                <input
                                    type="radio"
                                    name="export-detail"
                                    value="aggregated"
                                    checked={detail === 'aggregated'}
                                    onChange={() => setDetail('aggregated')}
                                    className={radioInputClass}
                                />
                                <span>
                                    <span className="ff-display text-sm">Aggregated</span>
                                    <span className="ff-mono text-[11px] text-[#141414]/55 block">
                                        Station totals only; no per-equipment breakdown.
                                    </span>
                                </span>
                            </label>
                        </div>
                    </div>

                    {error && (
                        <p className="ff-mono text-xs text-[#c2251a] mb-3">{error}</p>
                    )}

                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isExporting}
                            className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 border border-[#141414]/25 text-[#141414]/70 hover:bg-[#141414]/5 transition disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleExport}
                            disabled={isExporting}
                            className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] transition disabled:opacity-50"
                        >
                            {isExporting ? 'Exporting…' : 'Export'}
                        </button>
                    </div>
                </div>
            </article>
        </div>
    );
};

export default ExportDialog;
